import { useAppStore, type IAppStore } from '@/store';
import useAuth from '@/hooks/useAuth';
import useTextInput from './useTextInput';
import useMediaUpload from './useMediaUpload';
import useMediaRemove from './useMediaRemove';
import useDrivePicker from './useDrivePicker';
import useNativePicker from './useNativePicker';
import usePendingUploads from './usePendingUploads';

const selector = (store: IAppStore) => ({
  updateInput: store.updateInput,
  pendingUploads: store.pendingUploads,
  addPendingUpload: store.addPendingUpload,
  clearPendingUploads: store.clearPendingUploads,
});

export type UseMediaHandlersOptions = {
  uploadMedia: (file: File, id: string) => void;
};

export const useMediaHandlers = ({ uploadMedia }: UseMediaHandlersOptions) => {
  const { isAuthorized } = useAuth();
  const { updateInput, pendingUploads, addPendingUpload, clearPendingUploads } =
    useAppStore(selector);

  const { handleTextChange } = useTextInput(updateInput);
  const { handleMediaUpload } = useMediaUpload({
    uploadMedia,
    isAuthorized,
    addPendingUpload,
  });
  const { handleMediaRemove } = useMediaRemove(updateInput);
  const { handleSelectFromDrive, openDrivePickerForInput, pendingDriveAction } =
    useDrivePicker({ updateInput, isAuthorized });
  const { handleSelectFromNativePicker } = useNativePicker({
    updateInput,
    isAuthorized,
    addPendingUpload,
  });

  usePendingUploads({
    isAuthorized,
    pendingUploads,
    clearPendingUploads,
    uploadMedia,
    updateInput,
    openDrivePickerForInput,
    pendingDriveAction,
  });

  return {
    handleTextChange,
    handleMediaUpload,
    handleMediaRemove,
    handleSelectFromDrive,
    handleSelectFromNativePicker,
  };
};
